/**
 * moveEffects.logic — the PURE, Phaser-free decision core for the procedural
 * move-effect VFX engine. It answers the only questions that are not "draw it":
 *
 *   • what COLOUR does a type render in (vivid, battle-stage palette);
 *   • which CATEGORY template a move uses (beam / lunge-hit / aura);
 *   • whether a famous move has a SIGNATURE override (Hyper Beam, Ice Beam, …);
 *   • how long each phase lasts once `pace` (turbo) + `reduced` (reduced-motion)
 *     are folded in.
 *
 * Keeping this Phaser-free means the engine's branching is unit-testable in the
 * node env (vitest runs there; Phaser cannot load), exactly the repo's
 * pure-helper pattern (cf. topBarLayout / routeSprites). The Phaser module
 * (`moveEffects.ts`) imports this and only does the actual drawing/tweening.
 *
 * NOTE: this `TYPE_COLORS` is the VIVID battle-VFX palette and is deliberately
 * separate from the SOFT low-contrast pill palette in
 * `scenes/collection/parts.ts` — different job (a glowing beam vs a quiet tag),
 * so they intentionally differ.
 */
import type { PokemonType } from '@/shared';
import type { MoveCategory } from '@/shared';

/**
 * Vivid per-type VFX colour (0xRRGGBB). Tuned for a glowing beam / spark / aura
 * against the battle field — saturated and luminous, not the muted pill tags.
 */
export const TYPE_COLORS: Record<PokemonType, number> = {
  fire: 0xff5a2c, // orange-red
  water: 0x2f8cff, // blue
  ice: 0x7ff0ff, // cyan
  electric: 0xffe000, // yellow
  grass: 0x4cd14c, // green
  psychic: 0xff3cc8, // magenta
  poison: 0xa83cd6, // purple
  ground: 0xb07a3c, // brown
  rock: 0xd2b271, // tan
  flying: 0x8fd0ff, // sky-blue
  bug: 0xa8e02c, // lime
  ghost: 0x6a4fc0, // indigo
  dragon: 0x3050e0, // royal-blue
  dark: 0x4a4a5e, // slate
  steel: 0x9aa6b4, // grey
  fighting: 0xdc2f3c, // crimson
  normal: 0xf2e9c8, // cream
  fairy: 0xff8fd0, // pink
};

/** The visual template a move plays — one per category, plus the signatures. */
export type EffectTemplate = 'beam' | 'lunge-hit' | 'aura';

/** The known signature moves with a bespoke override (matched by id or name). */
export type SignatureKey =
  | 'hyper-beam'
  | 'ice-beam'
  | 'thunderbolt'
  | 'thunder'
  | 'flamethrower'
  | 'solar-beam'
  | 'hydro-pump'
  | 'blizzard';

/** Inputs the Phaser layer hands to the decision core (no scene, no geometry). */
export interface EffectDecisionInput {
  readonly moveId: string;
  readonly moveName: string;
  readonly type: PokemonType;
  readonly category: MoveCategory;
  /** Reduced-motion: collapse to a short, gentle version. */
  readonly reduced: boolean;
  /** Duration multiplier (turbo < 1 = faster). Clamped to a sane band. */
  readonly pace: number;
}

/**
 * The phase durations (ms) a template animates through. Not every template uses
 * every phase; the Phaser layer reads the ones it needs. Already pace/reduced
 * scaled so the drawing code never re-does the timing maths.
 */
export interface EffectTimings {
  /** Wind-up before the main visual (charge glow / aura ramp). */
  readonly charge: number;
  /** The travelling/active phase (beam flight, lunge, aura hold). */
  readonly travel: number;
  /** The impact burst / hit spark at the target. */
  readonly impact: number;
  /** Settle/fade-out of every spawned object. */
  readonly fade: number;
}

/** The complete, draw-ready plan the Phaser engine executes. */
export interface EffectPlan {
  readonly template: EffectTemplate;
  readonly signature: SignatureKey | null;
  readonly color: number;
  /** Some signatures override the base type colour (e.g. Hyper Beam → gold-white). */
  readonly coreColor: number;
  readonly timings: EffectTimings;
  /** True when the plan is the reduced-motion short form (a quick flash). */
  readonly reduced: boolean;
  /** Number of particles to stream; thinned hard under reduced motion. */
  readonly particleCount: number;
}

/** Vivid colour for a type. Falls back to `normal`'s cream for an unknown type. */
export function colorForType(type: PokemonType): number {
  return TYPE_COLORS[type] ?? TYPE_COLORS.normal;
}

/** The base template for a damage category. */
export function templateForCategory(category: MoveCategory): EffectTemplate {
  switch (category) {
    case 'special':
      return 'beam';
    case 'physical':
      return 'lunge-hit';
    case 'status':
      return 'aura';
    default:
      return 'aura';
  }
}

/**
 * Normalise a move id/name to a comparable token: lowercase, non-alphanumerics
 * collapsed to single spaces, trimmed. So "Hyper Beam", "hyper-beam", and
 * "hyperbeam" all reduce to the same family. (We compare both spaced and
 * de-spaced so "hyperbeam" still matches "hyper beam".)
 */
function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Signature lookup keyed by the normalized (spaced) name; values also de-spaced. */
const SIGNATURE_NAMES: ReadonlyArray<readonly [string, SignatureKey]> = [
  ['hyper beam', 'hyper-beam'],
  ['ice beam', 'ice-beam'],
  ['thunderbolt', 'thunderbolt'],
  ['thunder', 'thunder'],
  ['flamethrower', 'flamethrower'],
  ['solar beam', 'solar-beam'],
  ['solarbeam', 'solar-beam'],
  ['hydro pump', 'hydro-pump'],
  ['blizzard', 'blizzard'],
];

/**
 * Detect a signature override from a move's id and/or name. Returns the
 * `SignatureKey` or `null`. Matches on either field, tolerant of spacing /
 * punctuation / case ("Hyper Beam" == "hyper-beam" == "hyperbeam").
 *
 * IMPORTANT ordering: "thunderbolt" must be tested before "thunder" so a bolt
 * never degrades to plain Thunder; the table is ordered and we match the FIRST
 * key whose token is contained in the candidate.
 */
export function signatureFor(moveId: string, moveName: string): SignatureKey | null {
  const id = normalize(moveId);
  const name = normalize(moveName);
  const idTight = id.replace(/ /g, '');
  const nameTight = name.replace(/ /g, '');

  for (const [token, key] of SIGNATURE_NAMES) {
    const tokenTight = token.replace(/ /g, '');
    if (id === token || name === token || idTight === tokenTight || nameTight === tokenTight) {
      return key;
    }
  }
  // Fall back to a contained-token match (e.g. id "move-flamethrower").
  for (const [token, key] of SIGNATURE_NAMES) {
    const tokenTight = token.replace(/ /g, '');
    if (
      id.includes(token) ||
      name.includes(token) ||
      idTight.includes(tokenTight) ||
      nameTight.includes(tokenTight)
    ) {
      return key;
    }
  }
  return null;
}

/** A signature that renders as a beam, regardless of the move's category. */
const SIGNATURE_BEAMS: ReadonlySet<SignatureKey> = new Set<SignatureKey>([
  'hyper-beam',
  'ice-beam',
  'thunderbolt',
  'thunder',
  'flamethrower',
  'solar-beam',
  'hydro-pump',
  'blizzard',
]);

/** The template a resolved signature forces (all current signatures are beams). */
export function templateForSignature(sig: SignatureKey): EffectTemplate {
  return SIGNATURE_BEAMS.has(sig) ? 'beam' : 'beam';
}

/**
 * Some signatures override the bright "core" of the beam (the inner streak), even
 * though the outer glow stays type-coloured. Hyper Beam's core is gold-white;
 * Solar Beam's is a pale gold. Returns the override or `null` (use the type colour).
 */
export function coreColorForSignature(sig: SignatureKey | null): number | null {
  switch (sig) {
    case 'hyper-beam':
      return 0xfff4c0; // gold-white
    case 'solar-beam':
      return 0xfff080; // warm gold
    default:
      return null;
  }
}

/** Clamp pace to a sane band so a stray 0 / huge value can't break timings. */
export function clampPace(pace: number): number {
  if (!Number.isFinite(pace) || pace <= 0) return 1;
  return Math.max(0.1, Math.min(3, pace));
}

/**
 * Base (un-scaled, full-motion) phase timings for a template/signature. The
 * signatures get a touch more charge/impact to feel special.
 */
function baseTimings(template: EffectTemplate, sig: SignatureKey | null): EffectTimings {
  if (sig === 'hyper-beam') return { charge: 520, travel: 340, impact: 420, fade: 240 };
  if (sig === 'solar-beam') return { charge: 480, travel: 300, impact: 300, fade: 220 };
  if (sig === 'thunder' || sig === 'blizzard') {
    return { charge: 160, travel: 260, impact: 360, fade: 220 };
  }
  if (sig) return { charge: 120, travel: 300, impact: 300, fade: 200 };

  switch (template) {
    case 'beam':
      return { charge: 90, travel: 280, impact: 260, fade: 180 };
    case 'lunge-hit':
      return { charge: 0, travel: 170, impact: 220, fade: 150 };
    case 'aura':
      return { charge: 120, travel: 460, impact: 0, fade: 220 };
    default:
      return { charge: 90, travel: 280, impact: 220, fade: 180 };
  }
}

/** Scale a timings block by pace; reduced-motion collapses to a brief flash. */
function scaleTimings(base: EffectTimings, pace: number, reduced: boolean): EffectTimings {
  if (reduced) {
    // Reduced motion: a single quick, gentle flash — no charge, minimal travel.
    const q = Math.round(140 * pace);
    return { charge: 0, travel: Math.max(1, q), impact: Math.max(1, q), fade: Math.max(1, q) };
  }
  const s = (n: number): number => Math.max(1, Math.round(n * pace));
  return {
    charge: s(base.charge),
    travel: s(base.travel),
    impact: s(base.impact),
    fade: s(base.fade),
  };
}

/** Particle budget for a plan — generous at full motion, thinned hard when reduced. */
function particleBudget(
  template: EffectTemplate,
  sig: SignatureKey | null,
  reduced: boolean,
): number {
  if (reduced) return 6;
  if (sig === 'hyper-beam' || sig === 'thunder' || sig === 'blizzard') return 28;
  if (sig) return 22;
  switch (template) {
    case 'beam':
      return 18;
    case 'lunge-hit':
      return 12;
    case 'aura':
      return 16;
    default:
      return 14;
  }
}

/**
 * Resolve the full draw-ready plan for a move. This is the single entry point the
 * Phaser layer calls; everything above is a building block. Pure + deterministic
 * given its input, so the engine's whole branching tree is unit-testable.
 */
export function resolveEffectPlan(input: EffectDecisionInput): EffectPlan {
  const pace = clampPace(input.pace);
  const reduced = !!input.reduced;
  const signature = signatureFor(input.moveId, input.moveName);
  const template = signature
    ? templateForSignature(signature)
    : templateForCategory(input.category);

  const color = colorForType(input.type);
  const coreOverride = coreColorForSignature(signature);
  const coreColor = coreOverride ?? color;

  const timings = scaleTimings(baseTimings(template, signature), pace, reduced);
  const particleCount = particleBudget(template, signature, reduced);

  return { template, signature, color, coreColor, timings, reduced, particleCount };
}
